import { useMemo, useState } from "react";
import {
  Printer,
  Copy,
  Trash2,
  RotateCcw,
  MessageCircle,
  AlertCircle,
  Info,
  Check,
  X,
} from "lucide-react";
import { Shell } from "@/components/Shell";
import { computeResult, levelLabel } from "@/lib/scoring";
import {
  SYMPTOMS,
  IMMEDIATE_ALERTS,
  PRIORITY_ALERTS,
  MENSTRUAL_LABEL,
  AGE_RANGES,
} from "@/lib/questions";

// URL de WhatsApp genérica, sin datos clínicos.
const WHATSAPP_URL =
  "https://wa.me/522222388864?text=Hola%20Dr.%20Miguel%20%C3%81ngel%2C%20utilic%C3%A9%20Climaterio%20Claro%20y%20me%20gustar%C3%ADa%20solicitar%20informaci%C3%B3n%20para%20agendar%20una%20valoraci%C3%B3n.";

const LEVEL_STYLES = {
  green: {
    bg: "bg-[#E5EFDD]",
    border: "border-[#7A9463]",
    text: "text-[#2F4A1A]",
    dot: "bg-[#7A9463]",
    icon: <Check className="h-5 w-5" />,
  },
  yellow: {
    bg: "bg-[#FBEFC9]",
    border: "border-[#C7A227]",
    text: "text-[#5B4610]",
    dot: "bg-[#C7A227]",
    icon: <Info className="h-5 w-5" />,
  },
  "yellow-strong": {
    bg: "bg-[#F6DFA6]",
    border: "border-[#B4830F]",
    text: "text-[#4A3208]",
    dot: "bg-[#B4830F]",
    icon: <AlertCircle className="h-5 w-5" />,
  },
  neutral: {
    bg: "bg-[hsl(var(--arena))]",
    border: "border-[hsl(var(--olivo))]",
    text: "text-[hsl(var(--texto))]",
    dot: "bg-[hsl(var(--olivo))]",
    icon: <Info className="h-5 w-5" />,
  },
};

export function Result({ state, onRestart, onErase }) {
  const result = useMemo(() => computeResult(state), [state]);
  const [copied, setCopied] = useState(false);
  const [showWaModal, setShowWaModal] = useState(false);

  const meta = levelLabel(result.level);
  const style = LEVEL_STYLES[meta.color];

  const ageLabel =
    AGE_RANGES.find((a) => a.id === state.menstrual?.age_range)?.label ||
    "No especificado";
  const menstrualLabel = MENSTRUAL_LABEL(state.menstrual || {});

  const immediateHitTexts = IMMEDIATE_ALERTS.filter((q) =>
    result.immediateHits.includes(q.id),
  ).map((q) => q.text);
  const priorityHitTexts = PRIORITY_ALERTS.filter((q) =>
    result.priorityHits.includes(q.id),
  ).map((q) => q.text);

  const summaryText = buildSummary({
    ageLabel,
    menstrualLabel,
    level: meta.label,
    percent: result.percent,
    topSymptoms: result.topSymptoms,
    affectedAreas: result.affectedAreas,
    immediateHitTexts,
    priorityHitTexts,
    insufficient: result.level === "insuficiente",
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* ignore */
    }
  };

  const handleErase = () => {
    if (confirm("¿Deseas borrar todas tus respuestas de esta sesión?")) {
      onErase();
    }
  };

  return (
    <Shell showProgress={false}>
      <div className="print-full">
        <span className="cc-eyebrow">Tu resumen</span>
        <h1 className="font-serif text-3xl sm:text-4xl mt-3">
          Indicador orientativo de impacto
        </h1>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
          Esto no es una escala diagnóstica validada. Es una guía para preparar tu
          conversación con un profesional de salud.
        </p>

        {/* Alertas prioritarias / urgentes prevalecen */}
        {result.hasImmediate && (
          <Callout
            variant="urgent"
            title="Busca atención de urgencia ahora"
            testId="result-immediate-alert"
          >
            <ul className="mt-2 space-y-1 text-sm">
              {immediateHitTexts.map((t, i) => (
                <li key={i}>• {t}</li>
              ))}
            </ul>
          </Callout>
        )}
        {result.hasPriority && !result.hasImmediate && (
          <Callout
            variant="priority"
            title="Solicita valoración médica pronta"
            testId="result-priority-alert"
          >
            <ul className="mt-2 space-y-1 text-sm">
              {priorityHitTexts.map((t, i) => (
                <li key={i}>• {t}</li>
              ))}
            </ul>
            {result.priorityHits.includes("sangrado_post_12m") && (
              <p
                className="mt-4 text-sm leading-relaxed"
                data-testid="result-postmenopausal-bleeding-notice"
              >
                Cualquier sangrado vaginal después de 12 meses consecutivos sin
                menstruación debe ser evaluado por un profesional de salud, aunque
                sea escaso o haya ocurrido una sola vez. Esta respuesta no significa
                necesariamente que exista una enfermedad grave, pero conviene
                solicitar una valoración médica pronta.
              </p>
            )}
          </Callout>
        )}

        {/* Nivel: sólo cuando NO hay alertas (inmediatas ni prioritarias) */}
        {!result.hasImmediate && !result.hasPriority && (
          <div
            className={`mt-6 rounded-3xl border-2 p-6 ${style.bg} ${style.border} ${style.text} print-clean`}
            data-testid="result-level-card"
            data-level={result.level}
          >
            <div className="flex items-center gap-3">
              <span
                className={`h-3 w-3 rounded-full ${style.dot}`}
                aria-hidden="true"
              />
              <span className="cc-eyebrow text-current">Nivel</span>
            </div>
            <h2 className="font-serif text-2xl mt-2" data-testid="result-level">
              {meta.label}
            </h2>
            {result.level !== "insuficiente" && (
              <p className="text-sm mt-1 opacity-80" data-testid="result-percent">
                Porcentaje relativo: {result.percent}%
              </p>
            )}
            {result.level === "insuficiente" && (
              <p className="text-sm mt-2 opacity-90">
                Respondiste menos del 70% de los síntomas. Con esta información no
                podemos ofrecer una orientación clara. Puedes completar el resto o
                comentarlo en tu consulta.
              </p>
            )}
          </div>
        )}

        {/* Datos */}
        <div className="mt-4 cc-card p-5 print-clean">
          <p className="cc-eyebrow">Situación</p>
          <p className="mt-2 font-medium">Rango de edad: {ageLabel}</p>
          <p className="text-[hsl(var(--muted-foreground))]">{menstrualLabel}</p>
        </div>

        {result.topSymptoms.length > 0 && (
          <div className="mt-4 cc-card p-5 print-clean" data-testid="result-top-symptoms">
            <p className="cc-eyebrow">Tres síntomas con mayor intensidad</p>
            <ul className="mt-2 space-y-1 text-sm">
              {result.topSymptoms.map((s) => (
                <li key={s.id} className="flex justify-between gap-3">
                  <span>{s.text}</span>
                  <span className="cc-chip">{s.value} / 3</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.affectedAreas.length > 0 && (
          <div className="mt-4 cc-card p-5 print-clean">
            <p className="cc-eyebrow">Áreas afectadas</p>
            <ul className="mt-2 space-y-1 text-sm">
              {result.affectedAreas.map((a, i) => (
                <li key={i}>• {a}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 cc-card p-5 print-clean">
          <p className="cc-eyebrow">Para conversar en consulta</p>
          <ul className="mt-2 space-y-1 text-sm text-[hsl(var(--texto))]">
            <li>• Duración y patrón de los síntomas más intensos.</li>
            <li>• Cómo afectan tu sueño, trabajo y relaciones.</li>
            <li>• Historia menstrual reciente y cambios notados.</li>
            <li>• Antecedentes personales y familiares relevantes.</li>
            <li>• Preguntas específicas sobre las alertas señaladas, si las hay.</li>
          </ul>
        </div>

        <p className="mt-6 text-xs text-[hsl(var(--muted-foreground))] italic">
          Recuerda: Climaterio Claro no diagnostica. Este resumen se genera en tu
          navegador y no se almacena.
        </p>
      </div>

      {/* Acciones */}
      <div className="no-print mt-8 space-y-3" data-testid="result-actions">
        <button
          onClick={() => window.print()}
          className="cc-btn-primary w-full"
          data-testid="result-print-btn"
        >
          <Printer className="h-4 w-4" />
          Imprimir o guardar como PDF
        </button>
        <button
          onClick={handleCopy}
          className="cc-btn-secondary w-full"
          data-testid="result-copy-btn"
        >
          <Copy className="h-4 w-4" />
          {copied ? "Copiado" : "Copiar resumen sin datos personales"}
        </button>
        <button
          onClick={() => setShowWaModal(true)}
          className="cc-btn-accent w-full"
          data-testid="result-whatsapp-btn"
        >
          <MessageCircle className="h-4 w-4" />
          Solicitar una consulta por WhatsApp
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleErase}
            className="cc-btn-outline w-full"
            data-testid="result-erase-btn"
          >
            <Trash2 className="h-4 w-4" />
            Borrar respuestas
          </button>
          <button
            onClick={onRestart}
            className="cc-btn-outline w-full"
            data-testid="result-restart-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Comenzar de nuevo
          </button>
        </div>
      </div>

      {showWaModal && (
        <WhatsAppModal
          onClose={() => setShowWaModal(false)}
          onContinue={() => {
            // Enlace genérico sin respuestas clínicas.
            window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
            setShowWaModal(false);
          }}
        />
      )}
    </Shell>
  );
}

function Callout({ variant, title, children, testId }) {
  const isUrgent = variant === "urgent";
  return (
    <div
      className={`mt-6 rounded-3xl border-2 p-5 ${
        isUrgent
          ? "border-[hsl(var(--coral))] bg-[hsl(var(--coral))]/10"
          : "border-[#B4830F] bg-[#F6DFA6]"
      }`}
      data-testid={testId}
    >
      <div
        className={`flex items-center gap-2 ${
          isUrgent ? "text-[hsl(var(--coral))]" : "text-[#4A3208]"
        }`}
      >
        <AlertCircle className="h-5 w-5" aria-hidden="true" />
        <span className="cc-eyebrow text-current">
          {isUrgent ? "Urgencia" : "Prioridad"}
        </span>
      </div>
      <h2 className="font-serif text-xl mt-2 text-[hsl(var(--texto))]">{title}</h2>
      <div className="text-[hsl(var(--texto))]">{children}</div>
    </div>
  );
}

function WhatsAppModal({ onClose, onContinue }) {
  return (
    <div
      className="no-print fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="wa-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <span className="cc-eyebrow">Antes de continuar</span>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="h-8 w-8 rounded-full bg-[hsl(var(--arena))] flex items-center justify-center"
            data-testid="wa-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-serif text-2xl leading-snug">
          WhatsApp es un servicio externo
        </h3>
        <p className="mt-3 text-[hsl(var(--muted-foreground))] leading-relaxed">
          Climaterio Claro no enviará tus respuestas, síntomas ni resultado. Tú
          decides qué información compartir.
        </p>
        <div className="mt-6 space-y-3">
          <button
            onClick={onContinue}
            className="cc-btn-accent w-full"
            data-testid="wa-modal-continue"
          >
            Continuar a WhatsApp
          </button>
          <button
            onClick={onClose}
            className="cc-btn-outline w-full"
            data-testid="wa-modal-cancel"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function buildSummary({
  ageLabel,
  menstrualLabel,
  level,
  percent,
  topSymptoms,
  affectedAreas,
  immediateHitTexts,
  priorityHitTexts,
  insufficient,
}) {
  const lines = [];
  lines.push("Resumen orientativo — Climaterio Claro");
  lines.push("(No es diagnóstico ni escala validada)");
  lines.push("");
  lines.push(`Rango de edad: ${ageLabel}`);
  lines.push(`Situación menstrual: ${menstrualLabel}`);
  if (insufficient) {
    lines.push("Nivel: Orientación insuficiente (menos de 70% de síntomas respondidos)");
  } else {
    lines.push(`Nivel: ${level} (${percent}%)`);
  }
  if (topSymptoms.length) {
    lines.push("");
    lines.push("Síntomas con mayor intensidad:");
    topSymptoms.forEach((s) => lines.push(`- ${s.text}: ${s.value}/3`));
  }
  if (affectedAreas.length) {
    lines.push("");
    lines.push("Áreas afectadas:");
    affectedAreas.forEach((a) => lines.push(`- ${a}`));
  }
  if (immediateHitTexts.length) {
    lines.push("");
    lines.push("Alertas inmediatas:");
    immediateHitTexts.forEach((t) => lines.push(`- ${t}`));
  }
  if (priorityHitTexts.length) {
    lines.push("");
    lines.push("Alertas prioritarias:");
    priorityHitTexts.forEach((t) => lines.push(`- ${t}`));
  }
  lines.push("");
  lines.push("Aspectos para conversar en consulta:");
  lines.push("- Duración y patrón de síntomas");
  lines.push("- Impacto en sueño, trabajo y relaciones");
  lines.push("- Historia menstrual reciente");
  lines.push("- Antecedentes personales/familiares");
  return lines.join("\n");
}
